import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { AnimatePresence, motion } from 'framer-motion';
import { Link, useNavigate, useParams, useLocation } from 'react-router-dom';
import { LuSearch, LuVideo, LuUsers, LuLogOut, LuMenu, LuX, LuMapPin } from 'react-icons/lu';

import stateConfig from '../../stateConfig';
import { logOut } from '../../features/user/userSlice';

const NAV_ITEMS = [
  { id: 'search', label: 'AI Search', icon: LuSearch, match: ['search', 'bill', 'compare', 'votes', 'ai-report'] },
  { id: 'videos', label: 'Video Transcripts', icon: LuVideo, match: ['videos', 'video', 'create-clip'] },
  { id: 'lobby', label: 'Lobby', icon: LuUsers, match: ['lobby', 'lobbyist', 'company'] },
];

export const HEADER_HEIGHT = 56;

const MainHeader = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { state } = useParams();
  const [mobileOpen, setMobileOpen] = useState(false);
  const { features, redirectUrl, auth } = useSelector((s) => s.user);

  const config = stateConfig[state?.toLowerCase()] || {};
  const activeSegment = location.pathname.split('/').filter(Boolean)[1] || '';
  const isActive = (item) => item.match.includes(activeSegment);
  // Lobby pages are public, so anonymous visitors on them still get the lobby link.
  const visibleItems = NAV_ITEMS.filter(
    (item) => features?.includes(item.id) || (item.id === 'lobby' && isActive(item))
  );

  useEffect(() => setMobileOpen(false), [location.pathname]);

  const handleLogOut = () => {
    dispatch(logOut());
    navigate('/');
  };

  return (
    <div
      role="banner"
      className="fixed inset-x-0 top-0 z-30 h-14 border-b border-border bg-card/90 shadow-sm backdrop-blur-md print:hidden"
    >
      <div className="flex h-full w-full items-center gap-4 px-4 sm:px-6">
        {/* State logos are drawn for a dark banner, so they sit on a navy plate. */}
        <a
          href={redirectUrl}
          target="_top"
          aria-label="Home"
          className={`flex h-10 shrink-0 items-center rounded-lg px-3 ${config.bannerBlue ? 'bg-primary' : ''}`}
        >
          {config.logo ? (
            <img src={config.logo} alt="" className="h-[30px] w-auto max-w-[180px] object-contain" />
          ) : (
            <span className="font-serif text-lg font-semibold tracking-tight text-foreground">Telicon</span>
          )}
        </a>

        <nav aria-label="Primary" className="hidden w-auto items-center justify-start gap-1 md:flex">
          {visibleItems.map((item) => {
            const Icon = item.icon;
            const active = isActive(item);
            return (
              <Link
                key={item.id}
                to={`/${state}/${item.id}`}
                aria-current={active ? 'page' : undefined}
                className={`group inline-flex h-9 items-center gap-2 rounded-xl px-3.5 text-sm font-medium transition-colors ${
                  active ? 'bg-warning-muted text-foreground' : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                }`}
              >
                <Icon className="h-4 w-4 transition-transform group-hover:scale-110" aria-hidden="true" />
                {item.label}
              </Link>
            );
          })}
        </nav>

        <div className="ml-auto flex items-center gap-2">
          <span className="inline-flex h-9 items-center gap-1.5 rounded-md border border-border bg-background px-3 text-sm font-medium text-foreground">
            <LuMapPin className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
            {state?.toUpperCase()}
          </span>
          {auth === 'login' && (
            <button
              type="button"
              onClick={handleLogOut}
              className="hidden h-9 items-center gap-1.5 rounded-md border border-border bg-card px-3 text-sm font-medium text-foreground transition-colors hover:bg-muted md:inline-flex"
            >
              <LuLogOut className="h-4 w-4" aria-hidden="true" />
              Log out
            </button>
          )}

          <div className="relative md:hidden">
            <button
              type="button"
              aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={mobileOpen}
              onClick={() => setMobileOpen((prev) => !prev)}
              className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-border bg-card text-foreground hover:bg-muted"
            >
              {mobileOpen ? <LuX className="h-5 w-5" /> : <LuMenu className="h-5 w-5" />}
            </button>
            <AnimatePresence>
              {mobileOpen && (
                <motion.div
                  key="mobile-menu"
                  initial={{ opacity: 0, y: -6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -6 }}
                  transition={{ duration: 0.15, ease: 'easeOut' }}
                  className="absolute right-0 top-11 z-40 w-60 overflow-hidden rounded-xl border border-border bg-card p-2 shadow-lg"
                >
                  {visibleItems.map((item) => {
                    const Icon = item.icon;
                    return (
                      <Link
                        key={item.id}
                        to={`/${state}/${item.id}`}
                        className={`flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
                          isActive(item)
                            ? 'bg-warning-muted text-foreground'
                            : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                        }`}
                      >
                        <Icon className="h-4 w-4" aria-hidden="true" />
                        {item.label}
                      </Link>
                    );
                  })}
                  {auth === 'login' && (
                    <button
                      type="button"
                      onClick={handleLogOut}
                      className="mt-1 flex w-full items-center gap-3 border-t border-border px-3 pb-1.5 pt-3 text-sm font-medium text-muted-foreground hover:text-foreground"
                    >
                      <LuLogOut className="h-4 w-4" aria-hidden="true" />
                      Log out
                    </button>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MainHeader;
